import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(true);
  const passwordRef = useRef(null);

  // Verifica o token ao carregar a página
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/verify-reset-token/?token=${token}`);
        const data = await response.json();

        if (data.valid) {
          setTokenValid(true);
          setMaskedEmail(data.email);
        } else {
          setTokenValid(false);
          setError(data.error || 'Token inválido');
        }
      } catch (err) {
        setTokenValid(false);
        setError('Erro ao verificar token');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  // Foca no input de senha quando o token for válido
  useEffect(() => {
    if (tokenValid && !success && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [tokenValid, success]);

  // Validação rápida da força da senha
  const passwordStrength = (() => {
    if (!password) return { level: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (score <= 2) return { level: 1, label: 'Fraca', color: '#E83A3C' };
    if (score === 3) return { level: 2, label: 'Boa', color: '#F9B047' };
    return { level: 3, label: 'Forte', color: '#13983D' };
  })();

  const passwordsMatch = password && passwordConfirm && password === passwordConfirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== passwordConfirm) {
      setError('As senhas não coincidem.');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/reset-password/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          new_password: password,
          new_password_confirm: passwordConfirm,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(data.error || 'Erro ao redefinir senha.');
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor.');
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  // ============ LOADING (verificando token) ============
  if (verifying) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center font-['Inter',sans-serif] px-4 ${
          darkMode ? 'text-slate-200' : 'text-slate-700'
        }`}
        style={{
          background: darkMode
            ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
            : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%)',
        }}
      >
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 mx-auto mb-4 text-[#0974BA]" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
            Verificando link de recuperação...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center font-['Inter',sans-serif] px-4 ${
        darkMode ? 'text-slate-200' : 'text-slate-700'
      }`}
      style={{
        background: darkMode
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%)',
      }}
    >
      {/* Decorações de fundo */}
      <div
        className={`fixed w-[400px] h-[400px] bg-[#0974BA] rounded-full blur-[80px] -top-24 -right-24 -z-10 ${
          darkMode ? 'opacity-30' : 'opacity-20'
        }`}
      ></div>
      <div
        className={`fixed w-[300px] h-[300px] bg-[#2E4974] rounded-full blur-[80px] -bottom-12 -left-12 -z-10 ${
          darkMode ? 'opacity-30' : 'opacity-20'
        }`}
      ></div>

      {/* Botão de alternar tema */}
      <button
        type="button"
        onClick={() => setDarkMode(!darkMode)}
        aria-label={darkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
        className={`fixed top-5 right-5 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 z-10 ${
          darkMode
            ? 'bg-slate-800/80 border border-slate-600/15 text-yellow-400 hover:bg-slate-700/80'
            : 'bg-white/80 border border-slate-200 text-slate-600 hover:bg-slate-100/80 shadow-lg'
        }`}
        title={darkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
      >
        {darkMode ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      <div className="w-full max-w-[440px]">
        <div
          className={`relative backdrop-blur-xl border rounded-3xl px-8 py-10 sm:px-10 sm:py-12 overflow-hidden ${
            darkMode
              ? 'bg-slate-800/80 border-slate-600/15'
              : 'bg-white/90 border-slate-200 shadow-xl'
          }`}
        >
          {/* Barra gradiente no topo */}
          <div
            className="absolute top-0 left-0 right-0 h-1"
            style={{ background: 'linear-gradient(90deg, #0974BA, #2E4974)' }}
          ></div>

          {/* Logo + Título */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <img
                src="/iconSIDEB.svg"
                alt="SIDEB"
                className="w-full h-full object-contain"
              />
            </div>
            <div
              className={`text-3xl sm:text-4xl font-bold bg-clip-text text-transparent ${
                darkMode
                  ? 'bg-gradient-to-r from-gray-50 to-gray-400'
                  : 'bg-gradient-to-r from-slate-700 to-slate-500'
              }`}
            >
              SIDEB +
            </div>
            <div
              className={`text-sm mt-2 ${
                darkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              {success
                ? 'Senha redefinida'
                : !tokenValid
                  ? 'Link inválido'
                  : 'Definir nova senha'}
            </div>
          </div>

          {/* ============ TOKEN INVÁLIDO ============ */}
          {!tokenValid && !success && (
            <div className="text-center">
              <div
                className={`mx-auto mb-5 w-16 h-16 rounded-full flex items-center justify-center ${
                  darkMode ? 'bg-[#E83A3C]/15' : 'bg-[#E83A3C]/10'
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-9 w-9 text-[#E83A3C]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>

              <h2
                className={`text-xl font-semibold mb-2 ${
                  darkMode ? 'text-slate-100' : 'text-slate-800'
                }`}
              >
                Link inválido ou expirado
              </h2>

              <p
                className={`text-sm mb-6 ${
                  darkMode ? 'text-slate-400' : 'text-slate-600'
                }`}
              >
                {error || 'O link de recuperação não é válido ou já foi utilizado. Cada link expira em 1 hora.'}
              </p>

              <div className="flex flex-col gap-3">
                <Link
                  to="/forgot-password"
                  className="w-full rounded-xl py-3 text-white font-semibold text-sm text-center transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_rgba(101,140,191,0.4)]"
                  style={{ background: 'linear-gradient(135deg, #0974BA, #2E4974)' }}
                >
                  Solicitar novo link
                </Link>
                <Link
                  to="/login"
                  className={`text-sm transition-colors inline-flex items-center justify-center gap-2 ${
                    darkMode
                      ? 'text-slate-400 hover:text-slate-200'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Voltar para o login
                </Link>
              </div>
            </div>
          )}

          {/* ============ SUCESSO ============ */}
          {success && (
            <div className="text-center">
              <div
                className={`mx-auto mb-5 w-16 h-16 rounded-full flex items-center justify-center ${
                  darkMode ? 'bg-[#13983D]/15' : 'bg-[#13983D]/10'
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-9 w-9 text-[#13983D]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2
                className={`text-xl font-semibold mb-2 ${
                  darkMode ? 'text-slate-100' : 'text-slate-800'
                }`}
              >
                Senha redefinida!
              </h2>

              <p
                className={`text-sm mb-6 ${
                  darkMode ? 'text-slate-400' : 'text-slate-600'
                }`}
              >
                Sua senha foi alterada com sucesso. Você será redirecionado
                para o login em alguns segundos.
              </p>

              <Link
                to="/login"
                className="w-full inline-block rounded-xl py-3 text-white font-semibold text-sm text-center transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_rgba(101,140,191,0.4)]"
                style={{ background: 'linear-gradient(135deg, #0974BA, #2E4974)' }}
              >
                Ir para o login agora
              </Link>
            </div>
          )}

          {/* ============ FORMULÁRIO ============ */}
          {tokenValid && !success && (
            <>
              <p
                className={`text-sm text-center mb-6 ${
                  darkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Criando nova senha para{' '}
                <strong
                  className={darkMode ? 'text-slate-200' : 'text-slate-700'}
                >
                  {maskedEmail}
                </strong>
              </p>

              {error && (
                <div
                  role="alert"
                  className={`mb-5 p-4 border rounded-xl text-sm flex items-start gap-2 ${
                    darkMode
                      ? 'bg-[#E83A3C]/10 border-[#E83A3C]/30 text-[#E83A3C]'
                      : 'bg-[#E83A3C]/10 border-[#E83A3C]/20 text-[#E83A3C]'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-5">
                  <label
                    htmlFor="new-password"
                    className={`block text-xs font-medium mb-2 uppercase tracking-wide ${
                      darkMode ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    Nova Senha
                  </label>
                  <div className="relative">
                    <div
                      className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none ${
                        darkMode ? 'text-slate-500' : 'text-slate-400'
                      }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      ref={passwordRef}
                      id="new-password"
                      name="new-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="Digite sua nova senha"
                      className={`w-full border rounded-xl pl-12 pr-12 py-3.5 text-sm transition-all duration-300 focus:outline-none focus:border-[#0974BA] focus:ring-[3px] focus:ring-[#0974BA]/20 ${
                        darkMode
                          ? 'bg-slate-900/80 border-slate-600/20 text-slate-200 placeholder-slate-600'
                          : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-colors ${
                        darkMode
                          ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-700/50'
                          : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'
                      }`}
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Indicador de força da senha */}
                  {password && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex gap-1 flex-1">
                        <div
                          className="h-1 flex-1 rounded-full transition-colors"
                          style={{
                            backgroundColor:
                              passwordStrength.level >= 1
                                ? passwordStrength.color
                                : darkMode
                                  ? '#334155'
                                  : '#e2e8f0',
                          }}
                        />
                        <div
                          className="h-1 flex-1 rounded-full transition-colors"
                          style={{
                            backgroundColor:
                              passwordStrength.level >= 2
                                ? passwordStrength.color
                                : darkMode
                                  ? '#334155'
                                  : '#e2e8f0',
                          }}
                        />
                        <div
                          className="h-1 flex-1 rounded-full transition-colors"
                          style={{
                            backgroundColor:
                              passwordStrength.level >= 3
                                ? passwordStrength.color
                                : darkMode
                                  ? '#334155'
                                  : '#e2e8f0',
                          }}
                        />
                      </div>
                      <span
                        className="text-xs font-medium"
                        style={{ color: passwordStrength.color }}
                      >
                        {passwordStrength.label}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mb-5">
                  <label
                    htmlFor="confirm-password"
                    className={`block text-xs font-medium mb-2 uppercase tracking-wide ${
                      darkMode ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    Confirmar Senha
                  </label>
                  <div className="relative">
                    <div
                      className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none ${
                        darkMode ? 'text-slate-500' : 'text-slate-400'
                      }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      id="confirm-password"
                      name="confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      required
                      minLength={6}
                      placeholder="Confirme sua nova senha"
                      className={`w-full border rounded-xl pl-12 pr-12 py-3.5 text-sm transition-all duration-300 focus:outline-none focus:border-[#0974BA] focus:ring-[3px] focus:ring-[#0974BA]/20 ${
                        darkMode
                          ? 'bg-slate-900/80 border-slate-600/20 text-slate-200 placeholder-slate-600'
                          : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                      }`}
                    />
                    {/* Ícone de check quando as senhas batem */}
                    {passwordsMatch && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#13983D]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {passwordConfirm && !passwordsMatch && (
                    <p className="text-xs text-[#E83A3C] mt-1.5 flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      As senhas não coincidem
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !password || !passwordConfirm || !passwordsMatch}
                  className="w-full rounded-xl py-4 text-white font-semibold text-base transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_rgba(101,140,191,0.4)] active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                  style={{ background: 'linear-gradient(135deg, #0974BA, #2E4974)' }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Redefinindo...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Redefinir senha
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  )}
                </button>
              </form>

              <div className="text-center mt-6">
                <Link
                  to="/login"
                  className={`text-sm transition-colors inline-flex items-center gap-2 ${
                    darkMode
                      ? 'text-slate-400 hover:text-slate-200'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Voltar para o login
                </Link>
              </div>
            </>
          )}

          {/* Footer */}
          <p
            className={`text-center mt-8 text-xs ${
              darkMode ? 'text-slate-600' : 'text-slate-400'
            }`}
          >
            © {currentYear} SIDEB — Secretaria Municipal de Educação
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
